import React, { FC, useState } from 'react';
import { SubmitButton } from '@app/components/SubmitButton';
import {
  TransactionError,
  useAddress,
  useChainId,
  useSigner,
  useSwitchChain
} from '@thirdweb-dev/react';
import { Mode } from '@thirdweb-dev/chains';
import { CustomConnectWalletButton } from '@app/components/CustomConnectWalletButton';
import { useAllowances } from '@app/hooks/token/useAllowance';
import { useCartStore } from '@app/state/nftCartStore';
import { useBulkBuyNow } from '@app/hooks/nft/useBulkBuyItNow';
import { useApproval } from '@app/hooks/token/useApproval';
import { useCartTokensToApprove } from '@app/hooks/nft/useCartTokensToApprove';
import { queryClient } from '@app/context/AppContextWrapper';
import { QueryKeys } from '@app/constants/queryKeys';
import { useNftCartModal } from '@app/context/NftCartContext';
import { TransactionResultModal } from '@app/components/TransactionResultModal';
import {
  formatCurrenciesToCheckAllowance,
  CartSubtotals
} from '@app/helpers/nft/nftCartHelpers';
import { ApplicationConfig } from '@app/config/types';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';

interface Props {
  cartSubtotals: CartSubtotals;
  config: ApplicationConfig;
  listings: NFTMarketplaceListing[];
  // optionalPremiumTokenId?: string;
}

const EXPIRATION_MESSAGE = 'Please remove the expired items from the cart.';

export const SubmitSection: FC<Props> = ({
  cartSubtotals,
  listings,
  config
}) => {
  const { clearCart } = useCartStore();
  const address = useAddress();
  const chainId = useChainId();
  const switchChain = useSwitchChain();
  const signer = useSigner();
  const { mutate: approve, isLoading: isApproving } = useApproval(true);
  const [buyResult, setBuyResult] = useState<{
    status: 'success' | 'error';
    title: string;
    message: string;
    txHash?: string;
  } | null>(null);
  const { toggleModal } = useNftCartModal();
  const { bulkBuyNow, transactionHash, loading: isBuying } = useBulkBuyNow();

  const currenciesToCheck = formatCurrenciesToCheckAllowance(cartSubtotals);
  const allowancesQueries = useAllowances({
    tokens: currenciesToCheck,
    spenderAddress: config.CONTRACTS.NFT_MARKETPLACE
  });
  const isLoadingAllowances = allowancesQueries.some(query => query.isLoading);
  const tokensToApprove = useCartTokensToApprove({
    allowancesQueries,
    cartSubtotals,
    isLoadingAllowances
  });
  const someItemsAreExpired = listings.some(item => !item.isNFTAvailable);

  if (!address) {
    return <CustomConnectWalletButton />;
  }

  if (chainId !== Mode.chainId) {
    return (
      <SubmitButton
        label="Switch chain"
        isLoading={false}
        onClick={() => switchChain(Mode.chainId)}
        sx={{ marginTop: 0 }}
      />
    );
  }

  if (isLoadingAllowances) {
    return (
      <SubmitButton
        label="Loading..."
        isLoading
        onClick={() => switchChain(Mode.chainId)}
        sx={{ marginTop: 0 }}
      />
    );
  }

  const handleOnClickConfirm = async () => {
    try {
      const res = await bulkBuyNow({ listings });

      if (res?.transactionHash) {
        setBuyResult({
          status: 'success',
          txHash: res?.transactionHash as string,
          title: 'Success',
          message: 'The NFT has been successfully purchased'
        });
      } else if (res instanceof TransactionError) {
        if (res.raw.code !== 4001) {
          console.log(res);

          setBuyResult({
            status: 'error',
            txHash: '',
            title: 'Error',
            message: 'Transaction failed.'
          });
        }
      }
    } catch (e) {
      console.log(e);

      setBuyResult({
        status: 'error',
        txHash: '',
        title: 'Error',
        message: 'Transaction failed.'
      });
    }
  };

  const renderSubmitButton = () => {
    if (tokensToApprove.length > 0) {
      const { token, totalPrice } = tokensToApprove[0];

      return (
        <SubmitButton
          label={
            someItemsAreExpired
              ? EXPIRATION_MESSAGE
              : `Approve: ${token.name} spending`
          }
          isLoading={isApproving}
          disabled={someItemsAreExpired}
          sx={{ marginTop: 0 }}
          onClick={() => {
            approve({
              signer,
              token,
              amount: totalPrice,
              network: Mode,
              spenderAddress: config?.CONTRACTS.NFT_MARKETPLACE
            });
          }}
        />
      );
    } else {
      return (
        <SubmitButton
          label={someItemsAreExpired ? EXPIRATION_MESSAGE : 'Confirm'}
          disabled={someItemsAreExpired}
          isLoading={isBuying}
          sx={{ marginTop: 0 }}
          onClick={handleOnClickConfirm}
        />
      );
    }
  };

  return (
    <>
      {renderSubmitButton()}
      <TransactionResultModal
        isOpen={!!buyResult}
        status={buyResult?.status}
        title={buyResult?.title}
        message={buyResult?.message}
        txHash={buyResult?.txHash}
        onClose={async () => {
          setBuyResult(null);
          await queryClient.invalidateQueries({
            queryKey: [QueryKeys.GET_NFT_MARKETPLACE_LISTINGS]
          });
          clearCart();
          toggleModal(false);
        }}
      />
    </>
  );
};
